import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getTeacherStudents,
  createTeacherStudent,
  updateTeacherStudent,
  deleteTeacherStudent
} from '../services/teacherStudentService';
import Notification from '../components/Common/Notification';

export const TeacherStudentContext = createContext();

export const TeacherStudentProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [teacherStudents, setTeacherStudents] = useState([]);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllTeacherStudents = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTeacherStudents(authHeaders);
      setTeacherStudents(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching teacher-student associations.';
      showNotification(errorMessage, true);
    }
  };

  const addTeacherStudent = async (teacherStudentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newTeacherStudent = await createTeacherStudent(teacherStudentData, authHeaders);
      setTeacherStudents(prevTeacherStudents => [...prevTeacherStudents, newTeacherStudent]);
      showNotification('Teacher-student association added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding teacher-student association.';
      showNotification(errorMessage, true);
    }
  };

  const editTeacherStudent = async (id, teacherStudentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedTeacherStudent = await updateTeacherStudent(id, teacherStudentData, authHeaders);
      setTeacherStudents(prevTeacherStudents =>
        prevTeacherStudents.map(teacherStudent => (teacherStudent.id === id ? updatedTeacherStudent : teacherStudent))
      );
      showNotification('Teacher-student association updated successfully!', false);
      return updatedTeacherStudent;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating teacher-student association.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeTeacherStudent = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteTeacherStudent(id, authHeaders);
      setTeacherStudents(prevTeacherStudents => prevTeacherStudents.filter(teacherStudent => teacherStudent.id !== id));
      showNotification('Teacher-student association removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing teacher-student association.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllTeacherStudents();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <TeacherStudentContext.Provider
      value={{
        teacherStudents,
        fetchAllTeacherStudents,
        addTeacherStudent,
        editTeacherStudent,
        removeTeacherStudent,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </TeacherStudentContext.Provider>
  );
};

export const useTeacherStudent = () => useContext(TeacherStudentContext);
